import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Check, Copy, KeyRound, Mail, RefreshCw, Send, ShieldCheck,
} from 'lucide-react';
import { instances } from '../api';

/**
 * Die Zugänge einer Instanz — unser Betreiberkonto und das des Kunden.
 *
 * ══ WARUM ES DAS BRAUCHT ═══════════════════════════════════════════════════
 *
 * Die Funktionen dahinter gab es schon: `adminAccountFor`, `setAdminAccount`,
 * `customerAdminFor` und der Zugangslink. Nur führte kein Weg dorthin — sie
 * waren ausschliesslich über die Browser-Konsole auslösbar. Eine Funktion, die
 * man nur so erreicht, ist nicht fertig.
 *
 * ══ WAS HIER NICHT PASSIERT ════════════════════════════════════════════════
 *
 * Passwörter werden nicht angezeigt. Der PC gibt eines genau einmal heraus —
 * direkt nach dem Setzen — und danach nie wieder. Ein Feld „Passwort anzeigen"
 * wäre eine zweite Kopie des Kundenzugangs, die für immer in unserer Datenbank
 * liegt und in jedem Bildschirmfoto landet.
 *
 * Für den Kunden gibt es deshalb den Zugangslink: der geht an **seine** Adresse,
 * nicht über den Posteingang des Bearbeiters.
 */
export default function ZugaengePanel({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Das einmal sichtbare Passwort. Nicht im Zwischenspeicher, nur im Zustand. */
  const [einmal, setEinmal] = useState<{ wer: string; wert: string } | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  /** Probelauf: schickt an die eigene Adresse statt an den Kunden. */
  const [anMich, setAnMich] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-konten', instanceId],
    queryFn: () => instances.adminKonten(instanceId),
  });
  // Welcher Weg in eine laufende Instanz fuehrt, entscheidet das Backend --
  // es kennt das Produkt. Vorher riet die Oberflaeche und zeigte bei bryve
  // einen Knopf, der eine Fehlermeldung gab.
  const direktWeg = data?.direktWeg ?? 'keiner';

  function fehlerText(e: unknown): string {
    return (
      (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
      'Das hat nicht geklappt.'
    );
  }

  /**
   * Die Adresse des Betreiberkontos, im Bearbeiten.
   *
   * `null` heisst: nicht im Bearbeiten. Ein leerer String ist ein gueltiger
   * Zwischenstand und darf nicht dasselbe bedeuten.
   */
  const [adresseEntwurf, setAdresseEntwurf] = useState<string | null>(null);

  const adresseSetzen = useMutation({
    mutationFn: (email: string) => instances.setAdminKonto(instanceId, { target: 'master', email }),
    onSuccess: () => {
      setAdresseEntwurf(null);
      setFehler(null);
      setMeldung('Adresse gesetzt.');
      qc.invalidateQueries({ queryKey: ['admin-konten', instanceId] });
    },
    onError: (e: unknown) => setFehler(fehlerText(e)),
  });

  /**
   * Das Betreiberpasswort in der laufenden Instanz setzen.
   *
   * Erzeugt hier ein neues, setzt es dort und zeigt es einmal — sonst haette
   * man ein Passwort gesetzt, das man selbst nicht kennt.
   */
  const inDerInstanz = useMutation({
    mutationFn: async () => {
      const zeichen = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789-_';
      const roh = new Uint32Array(24);
      crypto.getRandomValues(roh);
      const neu = Array.from(roh, (n) => zeichen[n % zeichen.length]).join('');
      const r = await instances.betreiberPasswortSetzen(instanceId, neu);
      return { ...r, passwort: neu };
    },
    onSuccess: (r) => {
      if (!r.ok) { setFehler(r.meldung); return; }
      setFehler(null);
      setLink(null);
      setEinmal({ wer: 'Betreiberkonto (in der Instanz gesetzt)', wert: r.passwort });
      setMeldung(r.meldung);
      void qc.invalidateQueries({ queryKey: ['admin-konten', instanceId] });
    },
    onError: (e: unknown) => setFehler(fehlerText(e)),
  });

  const neuesPasswort = useMutation({
    mutationFn: (target: 'master' | 'customer') =>
      instances.setAdminKonto(instanceId, { target, resetPassword: true }),
    onSuccess: (r, target) => {
      setFehler(null);
      setLink(null);
      if (r.password) {
        setEinmal({ wer: target === 'master' ? 'admin@juni.com' : 'Kundenkonto', wert: r.password });
      }
      setMeldung(
        r.needsDeploy
          ? 'Passwort gesetzt. Es wirkt erst nach dem nächsten Ausrollen — und nur, wenn das Konto noch nicht existiert.'
          : 'Passwort gesetzt.',
      );
      void qc.invalidateQueries({ queryKey: ['admin-konten', instanceId] });
    },
    onError: (e) => { setMeldung(null); setEinmal(null); setFehler(fehlerText(e)); },
  });

  const schicken = useMutation({
    mutationFn: (modus: 'anzeigen' | 'setzen') =>
      instances.zugangslink(instanceId, modus, anMich),
    onSuccess: (r) => {
      setFehler(null);
      setEinmal(null);
      setMeldung(
        !r.gesendet
          ? 'Kein Mailversand eingerichtet — der Link steht unten und muss von Hand weitergegeben werden.'
          : r.probelauf
            ? `Probelauf an ${r.adresse} geschickt. Der Link ist echt und einmal einlösbar — wenn du ihn öffnest, ist er verbraucht. Für den Kunden danach einfach neu schicken.`
            : `Link an ${r.adresse} geschickt. Er gilt 24 Stunden und ist einmal einlösbar.`,
      );
      // Ohne SMTP ist der Link die einzige Spur. Mit SMTP zeigen wir ihn
      // trotzdem: der Bearbeiter soll sehen, was der Kunde bekommt.
      setLink(r.link);
      void qc.invalidateQueries({ queryKey: ['admin-konten', instanceId] });
    },
    onError: (e) => { setMeldung(null); setEinmal(null); setLink(null); setFehler(fehlerText(e)); },
  });

  if (isLoading) {
    return <div className="animate-pulse rounded-xl border border-slate-200 bg-surface p-6" />;
  }

  const master = data?.master ?? null;
  const kunde = data?.customer ?? null;

  return (
    <div className="space-y-4">
      {meldung && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{meldung}</p>
      )}
      {fehler && (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>
      )}

      {/* Das einmal sichtbare Passwort. Bewusst auffällig und mit dem Hinweis,
          dass es nicht wiederkommt — sonst schliesst jemand die Seite. */}
      {einmal && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">
            Neues Passwort für {einmal.wer} — nur jetzt sichtbar
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded-lg bg-slate-900 px-3 py-2 text-sm text-surface">
              {einmal.wert}
            </code>
            <button
              type="button"
              onClick={() => { void navigator.clipboard.writeText(einmal.wert); setKopiert(true); }}
              className="rounded-lg border border-amber-300 px-3 text-amber-800 hover:bg-amber-100"
              title="Kopieren"
            >
              {kopiert ? <Check size={15} /> : <Copy size={15} />}
            </button>
          </div>
          <p className="mt-2 text-xs text-amber-800">
            Der PC gibt es nicht wieder heraus. Wer es verpasst, setzt ein neues.
          </p>
        </div>
      )}

      {/* ── Unser Betreiberkonto ────────────────────────────────────────── */}
      <div className="rounded-xl border border-slate-200 bg-surface p-4">
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck size={16} className="text-brand-600" />
          <h2 className="text-sm font-semibold text-slate-900">Betreiberkonto</h2>
          <span className="text-xs text-slate-500">unser Weg hinein, wenn der Kunde anruft</span>
        </div>

        {!master ? (
          <p className="text-sm text-slate-500">
            Für dieses Produkt ist in der Vorlage kein Administratorkonto hinterlegt.
          </p>
        ) : (
          <>
            <dl className="mb-3 grid grid-cols-[10rem_1fr] gap-y-1.5 text-sm">
              <dt className="text-slate-500">Adresse</dt>
              {/* Vorher stand hier nur „— nicht gesetzt —". Das sagt, dass
                  etwas fehlt, aber nicht wo man es hinschreibt — und gesetzt
                  wurde die Adresse bisher nirgends in der Oberflaeche. Ohne
                  sie legt die Instanz das Konto gar nicht erst an. */}
              <dd className="text-slate-900">
                {adresseEntwurf === null ? (
                  <span className="flex flex-wrap items-center gap-2">
                    {master.email ?? <span className="text-amber-700">— nicht gesetzt —</span>}
                    <button
                      type="button"
                      onClick={() => setAdresseEntwurf(master.email ?? 'admin@juni.com')}
                      className="text-xs text-brand-700 underline decoration-dotted"
                    >
                      {master.email ? 'ändern' : 'jetzt setzen'}
                    </button>
                  </span>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">
                    <input
                      type="email"
                      autoFocus
                      value={adresseEntwurf}
                      onChange={(e) => setAdresseEntwurf(e.target.value)}
                      placeholder="admin@juni.com"
                      className="min-w-52 rounded border border-slate-300 px-2 py-1 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                    <button
                      type="button"
                      disabled={adresseSetzen.isPending || !adresseEntwurf.includes('@')}
                      onClick={() => adresseSetzen.mutate(adresseEntwurf.trim())}
                      className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-40"
                    >
                      Speichern
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdresseEntwurf(null)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600"
                    >
                      Abbrechen
                    </button>
                  </span>
                )}
              </dd>
              <dt className="text-slate-500">Passwort</dt>
              <dd className="text-slate-900">
                {master.hasPassword ? 'hinterlegt' : '— keins —'}
                {master.hasPassword && !master.deployed && (
                  <span className="ml-2 text-amber-700">noch nicht ausgerollt</span>
                )}
              </dd>
              <dt className="text-slate-500">Feld / Geheimnis</dt>
              <dd className="text-slate-500">
                <code className="text-xs">{master.emailField}</code> ·{' '}
                <code className="text-xs">{master.secretKey}</code>
              </dd>
            </dl>

            {/* Der Punkt, an dem sonst falsche Erwartungen entstehen. */}
            {!master.appliesOnDeploy && (
              <p className="mb-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Dieses Produkt wendet die Werte <strong>nicht</strong> bei jedem Start an. Ein
                  hier gesetztes Passwort erreicht ein bereits bestehendes Konto nicht.
                </span>
              </p>
            )}
            {master.appliesOnDeploy && master.hasPassword && (
              <p className="mb-3 flex gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Ein <strong>bestehendes</strong> Konto wird beim Ausrollen nie überschrieben —
                  sonst setzte jeder Neustart zurück, was der Kunde geändert hat. Ein neues
                  Passwort wirkt also nur, solange das Konto noch nicht angelegt ist.
                </span>
              </p>
            )}

            {/* ══ DIE STELLE, AN DER ES REGELMAESSIG VERWECHSELT WIRD ══════
                Zwei Knöpfe, die beide „Passwort" sagen und Verschiedenes tun.
                Wer den falschen nimmt, merkt es erst, wenn er sich nicht
                anmelden kann — und sucht den Fehler dann woanders. */}
            <div className="mb-3 overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">Weg</th>
                    <th className="px-3 py-1.5 font-medium">schreibt</th>
                    <th className="px-3 py-1.5 font-medium">wirkt</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium text-slate-800">
                      Neues Passwort erzeugen
                    </td>
                    <td className="px-3 py-1.5">in die Konfiguration im PC</td>
                    <td className="px-3 py-1.5">
                      beim nächsten Ausrollen — <strong>nur</strong>, wenn das Konto dann noch
                      nicht existiert
                    </td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium text-slate-800">
                      {direktWeg === 'bryve-karte'
                        ? 'Kennwort eines Admin-Kontos setzen'
                        : 'Jetzt in der Instanz setzen'}
                    </td>
                    <td className="px-3 py-1.5">
                      {direktWeg === 'bryve-karte'
                        ? 'über die Schnittstelle der Instanz'
                        : 'in die Datenbank der laufenden Instanz'}
                    </td>
                    <td className="px-3 py-1.5">
                      sofort, ohne Ausrollen — der einzige Weg, wenn das Konto schon da ist
                    </td>
                  </tr>
                </tbody>
              </table>
              {direktWeg === 'bryve-karte' && (
                <p className="border-t border-slate-100 bg-sky-50 px-3 py-1.5 text-[11px] leading-relaxed text-sky-900">
                  Der zweite Weg steht bei diesem Produkt <strong>weiter unten</strong> in einem
                  eigenen Abschnitt — er geht über bryves Schnittstelle und kann dort jedes
                  Admin-Konto, nicht nur dieses hier.
                </p>
              )}
              {direktWeg === 'keiner' && (
                <p className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] leading-relaxed text-slate-500">
                  Für dieses Produkt gibt es den zweiten Weg nicht: es ist nicht bekannt, in
                  welcher Form es Passwörter ablegt, und ein Hash im falschen Format ersetzt
                  ein funktionierendes Passwort durch keins.
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => neuesPasswort.mutate('master')}
                disabled={neuesPasswort.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={14} /> Neues Passwort erzeugen
              </button>

              {/* ══ DER WEG, WENN DAS KONTO SCHON DA IST ══════════════════
                  Der Knopf links schreibt in die .env fuer das naechste
                  Ausrollen — und erreicht ein BESTEHENDES Konto nie, weil ojfm
                  ein vorhandenes Passwort beim Start nicht anfasst. Wer sich
                  ausgesperrt hat, kommt darueber nicht hinein.

                  Dieser hier setzt es dort, wo das Konto liegt: in der
                  Datenbank der Instanz. Sofort gueltig, ohne Ausrollen. */}
              {direktWeg === 'hier' && master.email && master.deployed && (
                <button
                  type="button"
                  onClick={() => inDerInstanz.mutate()}
                  disabled={inDerInstanz.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                >
                  <KeyRound size={14} />
                  {inDerInstanz.isPending ? 'setzt…' : 'Jetzt in der Instanz setzen'}
                </button>
              )}
            </div>
            {direktWeg === 'hier' && master.email && master.deployed && (
              <p className="mt-2 text-xs leading-snug text-slate-400">
                Der zweite Weg geht an der Anwendung vorbei, direkt in ihre Datenbank — und nur
                für <code className="font-mono">{master.email}</code>. Er ist der einzige, der
                wirkt, wenn das Konto schon existiert.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Das Konto des Kunden ────────────────────────────────────────── */}
      <div className="rounded-xl border border-slate-200 bg-surface p-4">
        <div className="mb-3 flex items-center gap-2">
          <KeyRound size={16} className="text-brand-600" />
          <h2 className="text-sm font-semibold text-slate-900">Kundenadministrator</h2>
        </div>

        {!kunde?.supported ? (
          <p className="text-sm text-slate-500">
            Dieses Produkt kennt kein getrenntes Kundenkonto.
          </p>
        ) : (
          <>
            <dl className="mb-3 grid grid-cols-[10rem_1fr] gap-y-1.5 text-sm">
              <dt className="text-slate-500">Adresse</dt>
              <dd className="text-slate-900">
                {kunde.email ?? '— keine hinterlegt —'}
                {kunde.emailSource && (
                  <span className="ml-2 text-xs text-slate-500">aus: {kunde.emailSource}</span>
                )}
              </dd>
              <dt className="text-slate-500">Passwort</dt>
              <dd className="text-slate-900">{kunde.hasPassword ? 'hinterlegt' : '— keins —'}</dd>
            </dl>

            {!kunde.email && (
              <p className="mb-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Ohne Adresse lässt sich kein Link schicken. Sie kommt aus der technischen
                  Adresse des Kunden, ersatzweise der Rechnungsadresse — oder von Hand aus der
                  Konfiguration dieser Instanz.
                </span>
              </p>
            )}

            {/* Vor den Knöpfen, nicht daneben: er ändert, was sie tun. Ein
                Schalter neben einem Knopf wird als Zusatz gelesen, einer davor
                als Bedingung. */}
            <label className="mb-2 flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm">
              <input
                type="checkbox"
                checked={anMich}
                onChange={(e) => setAnMich(e.target.checked)}
              />
              <span className={anMich ? 'font-semibold text-slate-900' : 'text-slate-500'}>
                Probelauf — an meine eigene Adresse
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => schicken.mutate('anzeigen')}
                disabled={schicken.isPending || !kunde.email || !kunde.hasPassword}
                className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                title="Zeigt dem Kunden sein erzeugtes Passwort einmalig"
              >
                <Mail size={14} /> Passwort anzeigen lassen
              </button>
              <button
                type="button"
                onClick={() => schicken.mutate('setzen')}
                disabled={schicken.isPending || !kunde.email}
                className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                title="Der Kunde wählt selbst; der PC setzt es in der laufenden Instanz"
              >
                <Send size={14} /> Selbst setzen lassen
              </button>
              <button
                type="button"
                onClick={() => neuesPasswort.mutate('customer')}
                disabled={neuesPasswort.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw size={14} /> Neues erzeugen
              </button>
            </div>

            <p className="mt-3 text-xs text-slate-500">
              <strong>Anzeigen</strong> zeigt dem Kunden das erzeugte Passwort einmal — er
              ändert es danach selbst. <strong>Selbst setzen</strong> lässt ihn eines wählen;
              der PC setzt es über das Betreiberkonto in der laufenden Instanz und braucht
              dafür, dass diese erreichbar ist. Beide Links gelten 24 Stunden, sind einmal
              einlösbar, und ein zweiter Versand entwertet den ersten.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Der <strong>Probelauf</strong> schickt dieselbe Mail mit einem echten Link an
              dich statt an den Kunden — eine Attrappe würde nicht beantworten, ob der Weg
              trägt. Öffnest du den Link, ist er verbraucht; schick dem Kunden danach einfach
              einen neuen.
            </p>
          </>
        )}
      </div>

      {/* Der erzeugte Link. Auch bei erfolgreichem Versand sichtbar, damit der
          Bearbeiter weiss, was beim Kunden ankommt. */}
      {link && (
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Verschickter Link
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-900">
              {link}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(link)}
              className="rounded-lg border border-slate-300 px-3 text-slate-500 hover:text-slate-900"
              title="Kopieren"
            >
              <Copy size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
